import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';

import EditForm from './components/EditForm.jsx';
import Instructions from './components/Instructions.jsx';

import { getSketchfabModelByUid, updateSketchfabModel } from '../../../../../api/sketchfab.js';
import {
  getAnatomicalStructureBySketchfabUid,
  upsertAnatomicalStructureVectorToPinecone,
  updateAnatomicalStructure,
} from '../../../../../models/anatomical_structures.js';

const primaryColor = '#004591'.slice(1);

// Todo: Descomponer en subcomponentes: Viewer3D, Header, Dialogs, etc.

const AnatomicalStructureResourceDetail = ({ collectionId, structureId, backPath }) => {
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const sketchfabApiRef = useRef(null);

  // Modelo (Sketchfab REST)
  const [model, setModel] = useState(null);
  const [loadingModel, setLoadingModel] = useState(true);
  const [isErrorModel, setIsErrorModel] = useState(false);
  const [errorModel, setErrorModel] = useState('');

  // Descripción del backend
  const [backendDescription, setBackendDescription] = useState('');
  const [loadingDescription, setLoadingDescription] = useState(true);

  // Anotaciones
  const [annotations, setAnnotations] = useState([]);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [hasAnnotations, setHasAnnotations] = useState(false);

  // Viewer API
  const [loadingViewer, setLoadingViewer] = useState(true);
  const [isErrorViewer, setIsErrorViewer] = useState(false);
  const [errorViewer, setErrorViewer] = useState('');

  // Pinecone
  const [loadingPinecone, setLoadingPinecone] = useState(false);
  const [isErrorPinecone, setIsErrorPinecone] = useState(false);
  const [isSuccessPinecone, setIsSuccessPinecone] = useState(false);
  const [errorPinecone, setErrorPinecone] = useState('');

  // Modales
  const [indicationsModalOpen, setIndicationsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Edición
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [isErrorEdit, setIsErrorEdit] = useState(false);
  const [errorEdit, setErrorEdit] = useState('');
  const [isSuccessEdit, setIsSuccessEdit] = useState(false);

  // Reinicialización del visualizador
  const [viewerKey, setViewerKey] = useState(0);

  // Cargar modelo (Sketchfab) y descripción (backend) en paralelo
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoadingModel(true);
        setLoadingDescription(true);
        setErrorModel('');

        const [modelResult, backendResult] = await Promise.allSettled([
          getSketchfabModelByUid(structureId),
          getAnatomicalStructureBySketchfabUid(structureId),
        ]);

        if (!mounted) return;

        if (modelResult.status === 'fulfilled') {
          setModel(modelResult.value);
          setIsErrorModel(false);
        } else {
          setIsErrorModel(true);
          setErrorModel(
            modelResult.reason?.message ||
              'No se pudo obtener la información del modelo desde Sketchfab.'
          );
          return;
        }

        if (backendResult.status === 'fulfilled') {
          const backendData = backendResult.value;
          setBackendDescription(backendData.description || '');
        } else {
          setBackendDescription('');
        }
      } catch (err) {
        if (!mounted) return;

        setIsErrorModel(true);
        setErrorModel(
          err?.message || 'No se pudo obtener la información de la estructura anatómica.'
        );
        setBackendDescription('');
      } finally {
        if (mounted) {
          setLoadingModel(false);
          setLoadingDescription(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [structureId]);

  // Inicializar la Viewer API de Sketchfab cuando el iframe esté listo
  useEffect(() => {
    if (!model || !iframeRef.current) return undefined;

    setLoadingViewer(true);
    setIsErrorViewer(false);
    setErrorViewer('');

    const initializeViewer = () => {
      const iframe = iframeRef.current;
      if (!iframe || !window.Sketchfab) {
        setLoadingViewer(false);
        setIsErrorViewer(true);
        setErrorViewer('El visor de Sketchfab no está disponible.');
        return;
      }

      const client = new window.Sketchfab(iframe);

      client.init(structureId, {
        loading: function onLoading() {
          setLoadingViewer(true);
        },
        success: function onSuccess(api) {
          setLoadingViewer(false);
          setIsErrorViewer(false);
          setErrorViewer('');
          sketchfabApiRef.current = api;
          api.start();

          api.addEventListener('viewerready', function () {
            api.getAnnotationList((err, annotationsList) => {
              if (!err && annotationsList && annotationsList.length > 0) {
                setHasAnnotations(true);
                setAnnotations(annotationsList);
              } else {
                setHasAnnotations(false);
                setAnnotations([]);
              }
            });
          });
        },
        error: function onError(err) {
          setLoadingViewer(false);
          setIsErrorViewer(true);
          setErrorViewer(
            err?.message ||
              'No se pudo cargar el modelo 3D. Verifica que el modelo exista y esté disponible.'
          );
        },
        annotations_visible: showAnnotations ? 1 : 0,
        autostart: 1,
        ui_annotations: showAnnotations ? 1 : 0,
        ui_stop: 0,
        ui_fadeout: 0,
        ui_fullscreen: 0,
        ui_infos: 0,
        ui_inspector: 0,
        ui_settings: 0,
        ui_vr: 0,
        ui_color: primaryColor,
        ui_watermark: 0,
      });
    };

    if (window.Sketchfab) {
      initializeViewer();
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://static.sketchfab.com/api/sketchfab-viewer-1.12.1.js';
    script.async = true;

    script.onload = () => {
      initializeViewer();
    };

    script.onerror = () => {
      setLoadingViewer(false);
      setIsErrorViewer(true);
      setErrorViewer(
        'No se pudo cargar el visor de Sketchfab. Verifica tu conexión a internet e intenta nuevamente.'
      );
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      sketchfabApiRef.current = null;
    };
  }, [model, structureId, showAnnotations, viewerKey]);

  const handleBack = () => {
    navigate(backPath);
  };

  const handleCloseIndicationsModal = () => {
    setIndicationsModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setIsErrorEdit(false);
    setErrorEdit('');
    setIsSuccessEdit(false);
  };

  const handleToggleAnnotations = () => {
    setShowAnnotations(!showAnnotations);
  };

  const handleUploadToPinecone = async () => {
    if (!model) {
      return;
    }

    try {
      setLoadingPinecone(true);
      setIsErrorPinecone(false);
      setIsSuccessPinecone(false);
      setErrorPinecone('');

      const formattedAnnotations = annotations.map((annotation, index) => ({
        index: index + 1,
        name: annotation.name,
        content: annotation.content.raw,
      }));

      await upsertAnatomicalStructureVectorToPinecone({
        sketchfabUid: structureId,
        name: model.name,
        description: backendDescription || '',
        annotations: formattedAnnotations,
      });
      setIsSuccessPinecone(true);
    } catch (error) {
      setIsErrorPinecone(true);
      setErrorPinecone(error?.message || 'Error al subir vector a Pinecone.');
    } finally {
      setLoadingPinecone(false);
    }
  };

  const handleSaveEdit = async e => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('name');
    const description = formData.get('description');

    if (!model) {
      return;
    }

    if (!name || name.trim() === '') {
      setIsErrorEdit(true);
      setErrorEdit('El nombre es requerido');
      return;
    }

    if (!description || description.trim() === '') {
      setIsErrorEdit(true);
      setErrorEdit('La descripción es requerida');
      return;
    }
    try {
      setLoadingEdit(true);
      setIsErrorEdit(false);
      setErrorEdit('');
      setIsSuccessEdit(false);

      const [sketchfabResult, backendResult] = await Promise.allSettled([
        updateSketchfabModel(structureId, { name }),
        updateAnatomicalStructure(structureId, { description }),
      ]);

      if (sketchfabResult.status === 'fulfilled') {
        setModel(prev => ({ ...prev, name }));
      } else {
        setIsErrorEdit(true);
        setErrorEdit(
          `Error al actualizar nombre en Sketchfab: ${
            sketchfabResult.reason?.message || 'Error desconocido'
          }`
        );
      }

      if (backendResult.status === 'fulfilled') {
        setBackendDescription(description);
      } else {
        setIsErrorEdit(true);
        setErrorEdit(
          prev =>
            prev +
            (prev ? ' | ' : '') +
            `Error al actualizar descripción en backend: ${
              backendResult.reason?.message || 'Error desconocido'
            }`
        );
      }

      if (sketchfabResult.status === 'fulfilled' && backendResult.status === 'fulfilled') {
        setIsSuccessEdit(true);
      }
    } catch (error) {
      setIsErrorEdit(true);
      setErrorEdit(error?.message || 'Error inesperado al guardar los cambios.');
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleRetryViewer = () => {
    setIsErrorViewer(false);
    setErrorViewer('');
    setLoadingViewer(true);
    setViewerKey(prev => prev + 1);
  };

  if (loadingModel || loadingDescription) {
    return (
      <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={6}>
        <CircularProgress size={60} />
        <Typography variant='body1' color='text.secondary' sx={{ mt: 2 }}>
          Cargando información del modelo...
        </Typography>
      </Box>
    );
  }

  if (isErrorModel) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          <Typography variant='h6' gutterBottom>
            Error al cargar el modelo
          </Typography>
          <Typography variant='body2'>{errorModel}</Typography>
        </Alert>
        <Button variant='contained' onClick={handleBack} startIcon={<ArrowBackIosIcon />}>
          Volver
        </Button>
      </Box>
    );
  }

  if (!model) {
    return (
      <Box>
        <Alert severity='info' sx={{ mb: 2 }}>
          No se encontró la estructura anatómica solicitada.
        </Alert>
        <Button variant='contained' onClick={handleBack} startIcon={<ArrowBackIosIcon />}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <React.Fragment>
      {/* Header con breadcrumbs y botones de acción */}
      <Stack direction='column' spacing={2} mb={3}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Breadcrumbs>
            <Link
              underline='always'
              color='primary'
              component={RouterLink}
              to='/resources/anatomical-collections'>
              Estructuras Anatómicas 3D
            </Link>
            <Link underline='always' color='primary' component={RouterLink} to={backPath}>
              Colección
            </Link>
            {model && <Typography color='text.primary'>{model.name}</Typography>}
          </Breadcrumbs>

          <Stack direction='row' spacing={1}>
            {hasAnnotations && (
              <Tooltip title={showAnnotations ? 'Ocultar anotaciones' : 'Mostrar anotaciones'}>
                <IconButton onClick={handleToggleAnnotations}>
                  {showAnnotations ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>
            )}
            {model && (
              <Tooltip title='Subir vector a Pinecone'>
                <IconButton onClick={handleUploadToPinecone} disabled={loadingPinecone}>
                  {loadingPinecone ? <CircularProgress size={24} /> : <CloudUploadIcon />}
                </IconButton>
              </Tooltip>
            )}
            {model && (
              <Tooltip title='Editar información del modelo'>
                <IconButton onClick={() => setEditModalOpen(true)} disabled={loadingEdit}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Mostrar instrucciones'>
              <IconButton onClick={() => setIndicationsModalOpen(true)}>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {isSuccessPinecone && (
          <Alert severity='success' onClose={() => setIsSuccessPinecone(false)}>
            Vector subido a Pinecone exitosamente.
          </Alert>
        )}
        {isErrorPinecone && (
          <Alert severity='error' onClose={() => setIsErrorPinecone(false)}>
            {errorPinecone}
          </Alert>
        )}
      </Stack>

      {/* Viewer 3D */}
      <Box
        sx={{
          width: '100%',
          height: '70vh',
          minHeight: 500,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
        }}>
        {loadingViewer && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
            }}>
            <CircularProgress size={60} />
            <Typography variant='body1' color='text.secondary' sx={{ mt: 2 }}>
              Cargando modelo 3D...
            </Typography>
          </Box>
        )}

        {isErrorViewer && !loadingViewer && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              zIndex: 10,
              p: 3,
            }}>
            <Alert severity='error' sx={{ mb: 2, maxWidth: 600 }}>
              <Typography variant='h6' gutterBottom>
                Error al cargar el visor 3D
              </Typography>
              <Typography variant='body2'>{errorViewer}</Typography>
            </Alert>
            <Button variant='contained' onClick={handleRetryViewer}>
              Reintentar
            </Button>
          </Box>
        )}

        <iframe
          key={viewerKey}
          ref={iframeRef}
          id='sketchfab-api-frame'
          width='100%'
          height='100%'
          src={`https://sketchfab.com/models/${structureId}/embed`}
          frameBorder='0'
          allow='autoplay; fullscreen; vr'
          allowFullScreen
          title={model.name}
          style={{ display: isErrorViewer && !loadingViewer ? 'none' : 'block' }}
        />
      </Box>

      {/* Dialog de Instrucciones */}
      <Dialog
        open={indicationsModalOpen}
        onClose={handleCloseIndicationsModal}
        maxWidth='md'
        fullWidth>
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' spacing={1} alignItems='center'>
              <InfoIcon />
              <Typography variant='h6' fontWeight='bold'>
                Instrucciones
              </Typography>
            </Stack>
            <IconButton
              edge='end'
              color='inherit'
              onClick={handleCloseIndicationsModal}
              aria-label='close'>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Instructions />
        </DialogContent>
      </Dialog>

      {/* Dialog de Edición */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: handleSaveEdit,
        }}>
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' spacing={1} alignItems='center'>
              <EditIcon />
              <Typography variant='h6' fontWeight='bold'>
                Editar Estructura Anatómica
              </Typography>
            </Stack>
            <IconButton
              edge='end'
              color='inherit'
              onClick={handleCloseEditModal}
              disabled={loadingEdit}
              aria-label='close'>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <EditForm
            name={model?.name || ''}
            description={backendDescription}
            loading={loadingEdit}
          />
          {isSuccessEdit && (
            <Alert severity='success' sx={{ mb: 2 }}>
              Cambios guardados exitosamente.
            </Alert>
          )}
          {isErrorEdit && errorEdit && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setIsErrorEdit(false)}>
              {errorEdit}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={handleCloseEditModal}>
            Cancelar
          </Button>
          <Button type='submit' variant='contained' disabled={loadingEdit}>
            Guardar cambios
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default AnatomicalStructureResourceDetail;
